import { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence, useInView } from 'framer-motion'
import {
  AlertCircle,
  Layers,
  TrendingUp,
  Repeat,
  Eye,
  Users,
  CheckCircle,
  ArrowRight,
  ChevronDown,
  Activity,
  Zap,
  Brain,
  Shield,
  Lock,
  EyeOff,
  FileText,
  Clock,
  MessageSquare,
} from 'lucide-react'
import Navbar from '../components/layout/Navbar'
import Footer from '../components/layout/Footer'
import { usePageMeta } from '../hooks/usePageMeta'

// ─── Empty Room Card ─────────────────────────────────────────────────────────

const briefLines = [
  { type: 'header', text: 'Rithmo captured across your 6 meetings:' },
  { type: 'spacer' },
  { type: 'section', text: 'Decisions made (4)' },
  { type: 'bullet', text: "Sarah owns the Q3 rollout. Deadline: July 18." },
  { type: 'bullet', text: 'Budget increase approved at $40K ceiling.' },
  { type: 'bullet', text: 'Design review moved to async. Marcus to send loom.' },
  { type: 'bullet', text: 'Staging stays separate through launch.' },
  { type: 'spacer' },
  { type: 'section', text: 'Open questions (3)' },
  { type: 'bullet', text: 'Who covers support while Jordan is out?' },
  { type: 'bullet', text: 'Is the Figma handoff blocking engineering?' },
  { type: 'bullet', text: 'Timeline if the contract slips a week?' },
  { type: 'spacer' },
  { type: 'section', text: 'Commitments made (2)' },
  { type: 'bullet', text: 'You promised a pricing follow-up by Thursday.' },
  { type: 'bullet', text: 'Dev team committed to feature freeze Friday.' },
]

function EmptyRoomCard() {
  const [phase, setPhase] = useState(0)

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      while (!cancelled) {
        setPhase(0)
        await wait(1200)
        if (cancelled) break
        setPhase(1)
        await wait(1500)
        if (cancelled) break
        setPhase(2)
        await wait(1100)
        if (cancelled) break
        setPhase(3)
        await wait(5500)  // the empty room — let it land
        if (cancelled) break
        setPhase(4)
        await wait(3200)
        if (cancelled) break
        setPhase(5)
        await wait(4000)  // hold on the tagline
        if (cancelled) break
      }
    }
    run()
    return () => { cancelled = true }
  }, [])

  const briefOpacity = phase >= 5 ? 0.18 : phase >= 4 ? 1 : 0

  return (
    <div className="relative w-full max-w-lg mx-auto lg:mx-0">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.8 }}
        className="bg-[#0d0d0d] rounded-2xl p-6 shadow-premium overflow-hidden"
      >
        {/* Accusation */}
        <div className="mb-5 min-h-[52px]">
          <motion.p
            animate={{ opacity: phase >= 1 ? 1 : 0 }}
            transition={{ duration: 0.9 }}
            className="text-gray-200 text-sm font-light mb-2"
          >
            You had 6 meetings today.
          </motion.p>
          <motion.p
            animate={{ opacity: phase >= 2 ? 1 : 0 }}
            transition={{ duration: 0.9 }}
            className="text-gray-200 text-sm font-light"
          >
            Here is everything you captured.
          </motion.p>
        </div>

        {/* The void — blinking cursor */}
        <motion.div
          animate={{ opacity: phase === 3 ? 1 : 0 }}
          transition={{ duration: 0.3 }}
          className="mb-4"
        >
          <motion.span
            animate={{ opacity: phase === 3 ? [1, 0, 1] : 0 }}
            transition={{ duration: 0.85, repeat: Infinity }}
            className="inline-block w-[2px] h-5 bg-gray-500"
          />
        </motion.div>

        {/* Brief materializes — dims when tagline takes over */}
        <motion.div
          animate={{ opacity: briefOpacity }}
          transition={{ duration: phase >= 5 ? 1.2 : 0.2 }}
          className="font-mono text-xs leading-relaxed"
        >
          {briefLines.map((line, i) => {
            if (line.type === 'spacer') return <div key={i} className="h-2.5" />
            if (line.type === 'header') return (
              <motion.p
                key={i}
                className="text-gray-100 font-medium mb-3"
                initial={{ opacity: 0, x: -4 }}
                animate={{ opacity: phase >= 4 ? 1 : 0, x: phase >= 4 ? 0 : -4 }}
                transition={{ delay: i * 0.1, duration: 0.35 }}
              >
                {line.text}
              </motion.p>
            )
            if (line.type === 'section') return (
              <motion.p
                key={i}
                className="text-accent-400 text-[10px] uppercase tracking-widest mb-1.5"
                initial={{ opacity: 0, x: -4 }}
                animate={{ opacity: phase >= 4 ? 1 : 0, x: phase >= 4 ? 0 : -4 }}
                transition={{ delay: i * 0.1, duration: 0.35 }}
              >
                {line.text}
              </motion.p>
            )
            return (
              <motion.p
                key={i}
                className="text-gray-400 pl-2 mb-0.5"
                initial={{ opacity: 0, x: -4 }}
                animate={{ opacity: phase >= 4 ? 1 : 0, x: phase >= 4 ? 0 : -4 }}
                transition={{ delay: i * 0.1, duration: 0.35 }}
              >
                <span className="text-gray-600 mr-1.5">·</span>
                {line.text}
              </motion.p>
            )
          })}
        </motion.div>

        {/* Closing tagline — takes the stage */}
        <motion.div
          animate={{ opacity: phase >= 5 ? 1 : 0, y: phase >= 5 ? 0 : 6 }}
          transition={{ duration: 1.0, ease: 'easeOut' }}
          className="mt-6 text-center"
        >
          <p className="text-gray-500 text-[11px] uppercase tracking-widest mb-2">
            You were there.
          </p>
          <p className="text-accent-400 text-base font-medium tracking-wide">
            Rithmo did the rest.
          </p>
        </motion.div>
      </motion.div>
    </div>
  )
}

// ─── Startup Intelligence Visualization ──────────────────────────────────────

function StartupIntelligenceViz() {
  const signals = [
    { label: 'Late join detected: 4x this week', color: 'text-warning', dot: 'bg-warning' },
    { label: 'Decision captured: founder briefed', color: 'text-success', dot: 'bg-success' },
    { label: 'Founder talk ratio: elevated', color: 'text-error', dot: 'bg-error' },
    { label: 'Culture friction: emerging', color: 'text-error', dot: 'bg-error' },
    { label: 'Recurring blocker: 3rd time', color: 'text-warning', dot: 'bg-warning' },
    { label: 'Team alignment: monitored', color: 'text-accent-400', dot: 'bg-accent-400' },
  ]

  return (
    <div className="relative max-w-2xl mx-auto">
      <div className="flex items-center justify-center mb-8">
        <motion.div
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          className="relative w-28 h-28 rounded-full bg-gradient-primary flex items-center justify-center shadow-glow"
        >
          <Brain className="w-10 h-10 text-white" />
          <div className="absolute inset-0 rounded-full bg-gradient-primary opacity-20 animate-pulse-slow" />
        </motion.div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {signals.map((signal, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.12 }}
            className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/10 border border-white/10 backdrop-blur-sm"
          >
            <motion.div
              className={`w-2 h-2 rounded-full flex-shrink-0 ${signal.dot}`}
              animate={{ opacity: [1, 0.4, 1] }}
              transition={{ duration: 2, repeat: Infinity, delay: i * 0.3 }}
            />
            <span className={`text-xs font-medium ${signal.color}`}>{signal.label}</span>
          </motion.div>
        ))}
      </div>
    </div>
  )
}

// ─── Live Founder Card ────────────────────────────────────────────────────────

const wait = (ms) => new Promise((res) => setTimeout(res, ms))

const execRows = [
  { label: 'You attended', pct: 25, value: '2 decisions', color: 'bg-gray-300', textColor: 'text-gray-400' },
  { label: 'You skipped', pct: 75, value: '6 decisions', color: 'bg-success', textColor: 'text-success' },
]

function LiveFounderCard() {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.5 })
  const [phase, setPhase] = useState(0)
  const [cycleKey, setCycleKey] = useState(0)

  useEffect(() => {
    if (!isInView) return
    let cancelled = false
    const run = async () => {
      while (!cancelled) {
        setCycleKey((k) => k + 1)
        setPhase(0)
        await wait(5000)
        if (cancelled) break
        setPhase(1)
        await wait(6000)
        if (cancelled) break
        setPhase(2)
        await wait(6000)
      }
    }
    run()
    return () => { cancelled = true }
  }, [isInView])

  return (
    <div ref={ref} className="rounded-2xl border border-accent-500/20 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 bg-dark-600/90">
        <div className="flex items-center gap-2">
          <motion.div
            className="w-2 h-2 rounded-full bg-accent-400"
            animate={{ opacity: [1, 0.3, 1] }}
            transition={{ duration: 1.4, repeat: Infinity }}
          />
          <span className="text-xs font-semibold text-white">Rithmo · Execution Intelligence</span>
        </div>
        <span className="text-[10px] text-dark-200 font-mono">Engineering Sync · Last 8 Meetings</span>
      </div>

      <div className="bg-gradient-to-br from-primary-50/60 to-accent-50/40 p-5 space-y-4">
        {/* Execution bars — remount each cycle to re-trigger bar animation */}
        <div>
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-3">
            Decisions Closed · Same Meeting, Same Agenda
          </p>
          <div key={cycleKey} className="space-y-3">
            {execRows.map((row, i) => (
              <div key={row.label}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs text-gray-600">{row.label}</span>
                  <span className={`text-xs font-bold ${row.textColor}`}>{row.value}</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <motion.div
                    className={`h-full rounded-full ${row.color}`}
                    initial={{ width: 0 }}
                    animate={{ width: `${row.pct}%` }}
                    transition={{ duration: 1.1, ease: 'easeOut', delay: 0.3 + i * 0.4 }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Signal card — always in DOM, opacity only */}
        <motion.div
          animate={{ opacity: phase >= 1 ? 1 : 0.3 }}
          transition={{ duration: 0.5 }}
          className="bg-dark-700 rounded-xl p-4"
        >
          <div className="flex items-center gap-1.5 mb-2.5">
            <Zap className="w-3.5 h-3.5 text-accent-400 flex-shrink-0" />
            <span className="text-[10px] font-semibold text-accent-400 uppercase tracking-wider">
              Rithmo · Insight
            </span>
          </div>
          <div className="relative h-[52px]">
            <motion.p
              animate={{ opacity: phase === 0 ? 1 : 0 }}
              transition={{ duration: 0.3 }}
              className="absolute inset-0 text-sm text-dark-300 leading-snug"
            >
              Analyzing execution output across 8 Engineering Syncs...
            </motion.p>
            <motion.p
              animate={{ opacity: phase === 1 ? 1 : 0 }}
              transition={{ duration: 0.3 }}
              className="absolute inset-0 text-sm text-white leading-snug"
            >
              Your team closes 3x more decisions when they run this meeting. That is time worth reclaiming.
            </motion.p>
            <motion.p
              animate={{ opacity: phase === 2 ? 1 : 0 }}
              transition={{ duration: 0.3 }}
              className="absolute inset-0 text-sm text-success leading-snug"
            >
              Engineering Sync complete. 6 decisions captured. 9 action items assigned. Brief delivered.
            </motion.p>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

// ─── FAQ Accordion ────────────────────────────────────────────────────────────

function FaqItem({ question, answer }) {
  const [open, setOpen] = useState(false)

  return (
    <div className="border-b border-gray-100 last:border-0">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left"
      >
        <span className="font-semibold text-gray-900">{question}</span>
        <motion.span
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="flex-shrink-0 ml-4"
        >
          <ChevronDown className="w-5 h-5 text-gray-400" />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="answer"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <p className="pb-5 text-gray-600 leading-relaxed">{answer}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// ─── Page Data ────────────────────────────────────────────────────────────────

const painPoints = [
  {
    icon: <Clock className="w-6 h-6" />,
    title: 'There Are More Rooms Than Hours',
    description:
      'As your company grows, the meetings that need your judgment grow with it. You cannot attend everything, and every room still produces decisions, commitments, and risks that affect you.',
  },
  {
    icon: <AlertCircle className="w-6 h-6" />,
    title: 'The Most Important Decisions Rarely Get Escalated',
    description:
      'Teams move fast. Scope gets redefined, commitments get made, and strategy gets reinterpreted in meetings you were not in. Most of it never reaches you until it is already expensive to reverse.',
  },
  {
    icon: <Eye className="w-6 h-6" />,
    title: 'Your Culture Is Forming in Rooms You Are Not In',
    description:
      'The patterns, the friction, and the energy that define your company are taking shape in meetings every day. Staying connected to that as you scale is one of the hardest parts of the job.',
  },
  {
    icon: <MessageSquare className="w-6 h-6" />,
    title: 'Every Founder Needs a Chief of Staff. Few Can Hire One.',
    description:
      'A great Chief of Staff attends the meetings you cannot, surfaces what you need to know, and keeps the org moving with your judgment in the room. It is one of the highest leverage roles a founder can fill.',
  },
]

const features = [
  {
    title: 'Know What Happened While You Were Somewhere Else',
    description:
      'When you can\'t attend a meeting, or shouldn\'t, Rithmo covers it. Every decision, action item, and key output gets captured and briefed to you automatically. Your team never has to stop and catch you up again.',
    icon: <Eye className="w-6 h-6" />,
    visual: (
      <div className="card space-y-3">
        <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">
          Meetings This Week · Founder Coverage
        </p>
        {[
          { name: 'Product Sprint Planning', note: 'Rithmo briefed you', covered: true },
          { name: 'Eng Architecture Review', note: 'Rithmo briefed you', covered: true },
          { name: 'Sales Pipeline Review', note: 'You attended', covered: false },
          { name: 'Leadership Weekly', note: 'You attended', covered: false },
        ].map((row) => (
          <div key={row.name} className="flex items-center justify-between gap-3">
            <span className="text-sm text-gray-700 flex-1">{row.name}</span>
            <span className={`text-xs font-medium ${row.covered ? 'text-accent-500' : 'text-gray-400'}`}>
              {row.note}
            </span>
          </div>
        ))}
        <div className="pt-2 border-t border-gray-100">
          <span className="badge-accent text-xs">2 of 4 meetings. Rithmo covered the rest.</span>
        </div>
      </div>
    ),
  },
  {
    title: 'See Friction Before It Becomes a Culture Problem',
    description:
      'Communication patterns, disengagement, and team dynamics compound quietly. Some of the most cited startup culture failures were visible in the meetings long before anyone named the problem. Rithmo surfaces them while there\'s still time to act.',
    icon: <Repeat className="w-6 h-6" />,
    visual: (
      <div className="card space-y-3">
        <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">
          Recurring Discussions · Last 30 Days
        </p>
        {[
          { topic: 'API ownership clarity', count: 4 },
          { topic: 'Launch scope alignment', count: 3 },
          { topic: 'Hiring process ownership', count: 3 },
        ].map((row) => (
          <div key={row.topic} className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Repeat className="w-3.5 h-3.5 text-warning flex-shrink-0" />
              <span className="text-sm text-gray-700">{row.topic}</span>
            </div>
            <span className="text-xs font-semibold text-warning">x{row.count}</span>
          </div>
        ))}
        <div className="pt-2 border-t border-gray-100">
          <span className="badge-warning text-xs">3 discussions repeating without resolution</span>
        </div>
      </div>
    ),
  },
  {
    title: 'Your Team Executes More When You Trust Them With This One',
    description:
      'Rithmo tracks decisions made, action items closed, and blockers resolved across every meeting, with you and without you. When the data shows your team ships more without you in the room, that is not a problem. That is time you can reclaim.',
    icon: <TrendingUp className="w-6 h-6" />,
    visual: (
      <div className="card space-y-4">
        <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">
          Engineering Sync · Execution Output · Last 12 Meetings
        </p>
        <div className="grid grid-cols-3 gap-2">
          <div />
          <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider text-center">With you</p>
          <p className="text-[10px] text-accent-600 font-semibold uppercase tracking-wider text-center">Without you</p>
        </div>
        {[
          { label: 'Decisions made', withYou: 2, without: 6 },
          { label: 'Action items closed', withYou: 3, without: 9 },
          { label: 'Blockers resolved', withYou: 1, without: 5 },
        ].map((row) => (
          <div key={row.label} className="grid grid-cols-3 gap-2 items-center">
            <span className="text-xs text-gray-600 leading-tight">{row.label}</span>
            <span className="text-sm font-semibold text-gray-300 text-center">{row.withYou}</span>
            <span className="text-sm font-bold text-accent-600 text-center">{row.without}</span>
          </div>
        ))}
        <div className="pt-2 border-t border-gray-100">
          <span className="badge-accent text-xs">Your team executes more when they own this room</span>
        </div>
      </div>
    ),
  },
  {
    title: 'Catch Decisions Before They Become Misalignment',
    description:
      'When teams make key decisions in meetings you weren\'t in, Rithmo surfaces them before they become expensive to undo. No chase down emails, no surprise pivots that contradict what you thought was settled.',
    icon: <CheckCircle className="w-6 h-6" />,
    visual: (
      <div className="card space-y-3">
        <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">
          Action Items · Leadership Sync
        </p>
        {[
          { item: 'Define v2 scope', owner: 'Jamie R.', resolved: true },
          { item: 'Confirm launch date', owner: 'Unassigned', resolved: false },
          { item: 'Align sales messaging', owner: 'Unassigned', resolved: false },
        ].map((a) => (
          <div key={a.item} className="flex items-center justify-between gap-3">
            <span className="text-sm text-gray-700 flex-1">{a.item}</span>
            <span className={`text-sm font-medium ${a.resolved ? 'text-success' : 'text-error'}`}>
              {a.owner}
            </span>
          </div>
        ))}
        <div className="pt-2 border-t border-gray-100">
          <span className="badge-error text-xs">2 of 3 items unowned</span>
        </div>
      </div>
    ),
  },
]

const outcomes = [
  'Free Your Calendar for the Work Only a Founder Can Do',
  'Know Every Key Decision Before It Becomes a Problem',
  'Stay Connected to Your Culture as Your Company Scales',
  'Show Up Where You Create the Most Value',
  'Get Briefed in 90 Seconds, Not 20 Minutes',
  'Build a Company That Scales With You, Not Around You',
]

const exampleInsights = [
  {
    text: 'A product decision stalled for two weeks because it required founder context that was never in the room. Rithmo identified the blocker and flagged it before the team lost another sprint cycle.',
    icon: <AlertCircle className="w-4 h-4" />,
  },
  {
    text: 'Rithmo detected that engineering and sales were making conflicting assumptions about Q3 launch scope in separate meetings. Neither team knew. The founder found out before it became a build and rebuild problem.',
    icon: <Layers className="w-4 h-4" />,
  },
  {
    text: 'Rithmo showed that engineering\'s output, decisions made and action items closed, was consistently higher in weeks the founder skipped their standup. That data gave the founder a new framework for deciding which meetings actually needed them.',
    icon: <TrendingUp className="w-4 h-4" />,
  },
  {
    text: 'A pricing strategy discussion surfaced in four different meetings across three teams over six weeks, each time without resolution. Rithmo connected the pattern and surfaced it as a founder level decision that had been accidentally delegated without authority.',
    icon: <Repeat className="w-4 h-4" />,
  },
]

const workflowSteps = [
  { label: 'Your Teams Meet Every Day', icon: <Clock className="w-5 h-5" /> },
  { label: 'Rithmo Is in Every Room From Minute One', icon: <Eye className="w-5 h-5" /> },
  { label: 'Decisions, Signals, and Patterns Captured', icon: <Activity className="w-5 h-5" /> },
  { label: 'Founder Brief Delivered Automatically', icon: <Zap className="w-5 h-5" /> },
  { label: 'Founder Redirects Time to Highest Leverage', icon: <TrendingUp className="w-5 h-5" /> },
  { label: 'Company Scales Without Founder as Bottleneck', icon: <Brain className="w-5 h-5" /> },
]

const faqs = [
  {
    question: 'I already have a Chief of Staff. How is Rithmo different?',
    answer:
      'A human CoS can attend a handful of your most important meetings. Rithmo is in all of them, simultaneously, from the first second, and it never has competing priorities, a calendar conflict, or a bad day.',
  },
  {
    question: 'How does Rithmo help me stay across meetings I cannot attend?',
    answer:
      'Rithmo joins every meeting automatically and delivers a structured brief: what was decided, what was committed to, what risks surfaced, and what needs your attention. You get full coverage of every room without being in any of them.',
  },
  {
    question: 'What if I want to attend fewer meetings without losing visibility?',
    answer:
      'That is exactly what Rithmo is built for. Rithmo gives you complete coverage of every meeting so your calendar decisions can be about where you create the most value, not about keeping up with everything happening across your company.',
  },
  {
    question: 'What is the Executive AI Proxy?',
    answer:
      'Rithmo is building toward a future where it can represent you in meetings, operating with your judgment, your values, and your communication style, so you can be strategically present where it matters most. It\'s coming.',
  },
  {
    question: 'Does Rithmo replace our current meeting platform?',
    answer:
      'No. Rithmo works alongside existing platforms like Zoom, Google Meet, and Microsoft Teams. No workflow disruption, no migration.',
  },
  {
    question: 'Is this a monitoring tool?',
    answer:
      'Rithmo is a founder intelligence platform, not employee surveillance. It helps founders understand organizational health, including communication patterns, decision quality, and team alignment, not track individual behavior for performance management.',
  },
]

// ─── Page ─────────────────────────────────────────────────────────────────────

function FoundersPage() {
  usePageMeta({
    title: 'Rithmo for Founders and CEOs: Your AI Chief of Staff',
    description: 'Rithmo is the AI Chief of Staff that gives founders the organizational intelligence they need to scale. Decisions captured, risks surfaced, and nothing important missed.',
  })
  return (
    <div className="min-h-screen bg-white">
      <Navbar transparent />

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary-50/50 to-white" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary-500/10 rounded-full blur-3xl animate-pulse-slow" />
        <div
          className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent-500/10 rounded-full blur-3xl animate-pulse-slow"
          style={{ animationDelay: '2s' }}
        />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left: copy */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="mb-4">
                <span className="badge-primary text-sm">For Founders & Startup Teams</span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                The AI Chief of Staff{' '}
                <span className="gradient-text">Every Founder Deserves.</span>
              </h1>

              <p className="text-lg md:text-xl text-gray-600 mb-10 leading-relaxed">
                Rithmo attends every meeting, captures every decision, tracks every commitment,
                and surfaces what needs your attention, so you can run a bigger company with
                the same clarity you had when it was small.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Link to="/signup" className="btn-primary text-lg px-8 py-4 text-center">
                  Get Early Access
                </Link>
              </div>

            </motion.div>

            {/* Right: Empty Room card */}
            <div className="flex justify-center lg:justify-end">
              <EmptyRoomCard />
            </div>
          </div>
        </div>
      </section>

      {/* ── Pain Points ───────────────────────────────────────────────────── */}
      <section className="pt-12 pb-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16 max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Scaling Changes Everything.{' '}
              <span className="gradient-text">Your Intelligence Layer Needs to Keep Up.</span>
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              Every founder reaches a point where they cannot be everywhere their company needs them.
              That is not a failure. It is the definition of scale. The question is whether you
              have the intelligence to stay ahead of it.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {painPoints.map((point, i) => (
              <motion.div
                key={point.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="card-interactive group"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-primary flex items-center justify-center mb-4 shadow-glow-sm group-hover:shadow-glow transition-shadow duration-300 text-white">
                  {point.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{point.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{point.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Autonomous Action ─────────────────────────────────────────────── */}
      <section className="py-24 bg-gray-50 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-white via-gray-50 to-white" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Data That Helps You{' '}
                <span className="gradient-text">Show Up Where It Matters Most.</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                Rithmo tracks participation, decisions, and output across every meeting, with
                you and without you. That data gives you something most founders never have: an
                objective picture of where your time creates the most value, and where your team
                is better served running the room themselves.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15 }}
              className="space-y-3"
            >
              {/* Step 1 */}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="bg-white rounded-2xl border border-accent-500/20 shadow-sm p-5"
              >
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2 h-2 rounded-full bg-accent-400 animate-pulse" />
                  <span className="text-xs font-semibold text-accent-600 uppercase tracking-widest">
                    Rithmo Insight · Engineering Sync
                  </span>
                </div>
                <p className="text-4xl font-bold gradient-text mb-1">3x</p>
                <p className="text-sm text-gray-500 mb-3">
                  More decisions closed when your team runs this meeting
                </p>
                <div className="flex items-center gap-3">
                  <button className="px-4 py-2 rounded-lg bg-gradient-primary text-white text-xs font-semibold shadow-glow-sm pointer-events-none">
                    Let Rithmo Cover It
                  </button>
                  <button className="px-4 py-2 rounded-lg border border-gray-200 text-gray-500 text-xs font-medium pointer-events-none">
                    Dismiss
                  </button>
                </div>
              </motion.div>

              <div className="flex justify-center">
                <div className="w-px h-5 bg-gray-200" />
              </div>

              {/* Step 2 */}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
                className="bg-success/5 rounded-2xl border border-success/20 p-4"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded-full bg-success flex items-center justify-center flex-shrink-0">
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                        <path
                          d="M1 4l2.5 2.5L9 1"
                          stroke="white"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <span className="text-sm font-semibold text-gray-800">
                      Rithmo Covering · Engineering Sync This Thursday
                    </span>
                  </div>
                </div>
              </motion.div>

              <div className="flex justify-center">
                <div className="w-px h-5 bg-gray-200" />
              </div>

              {/* Step 3: Live animated card */}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.6 }}
              >
                <LiveFounderCard />
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Features ──────────────────────────────────────────────────────── */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-white via-primary-50/30 to-white" />
        <div className="absolute -top-20 right-10 w-72 h-72 bg-accent-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-10 w-80 h-80 bg-primary-500/10 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              Founder Intelligence.{' '}
              <span className="gradient-text">Not Just Meeting Notes.</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Rithmo gives founders what no recording or summary ever could: organizational
              intelligence, the patterns, dynamics, and outputs that tell you how your company is
              really running.
            </p>
          </motion.div>

          <div className="space-y-20">
            {features.map((feature, i) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className={`grid lg:grid-cols-2 gap-10 items-center ${
                  i % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
                }`}
              >
                <div className={i % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="w-12 h-12 rounded-xl bg-gradient-primary flex items-center justify-center mb-4 shadow-glow-sm text-white">
                    {feature.icon}
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-lg text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
                <div className={i % 2 === 1 ? 'lg:col-start-1' : ''}>{feature.visual}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Dark Intelligence Section ──────────────────────────────────────── */}
      <section className="py-24 bg-gradient-dark relative overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-accent-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-64 h-64 bg-primary-500/10 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
              The Intelligence Layer{' '}
              <span className="gradient-text">Every Founder Deserves</span>
            </h2>
            <p className="text-lg text-dark-100 max-w-2xl mx-auto leading-relaxed">
              The best run companies have a Chief of Staff, someone who attends the meetings you
              cannot, captures every decision, surfaces the friction before it compounds, and keeps
              nothing important from the leader. Most founders cannot afford that person. Rithmo is
              the first version of that role that scales with your company, learning your priorities
              and making sure nothing that matters falls through.
            </p>
          </motion.div>

          <StartupIntelligenceViz />

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.8 }}
            className="text-center text-dark-200 mt-10 text-lg"
          >
            The goal isn't to record meetings.
            <br />
            <span className="text-white font-medium">
              It's to give every founder the organizational intelligence that used to require a team to produce.
            </span>
          </motion.p>
        </div>
      </section>

      {/* ── Example Insights ──────────────────────────────────────────────── */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                The Patterns Are Already There.{' '}
                <span className="gradient-text">Rithmo Surfaces Them.</span>
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                The patterns that define how healthy, or unhealthy, a startup is tend to show up
                in meetings weeks before anyone names them out loud. Rithmo catches them early,
                when there's still time to act.
              </p>
              <div className="flex flex-wrap gap-3">
                {['Founder Leverage', 'Culture Health', 'Decision Clarity', 'Team Dynamics'].map((tag) => (
                  <span key={tag} className="badge-accent">
                    {tag}
                  </span>
                ))}
              </div>
            </motion.div>

            <div className="space-y-4">
              {exampleInsights.map((insight, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.12 }}
                  className="card flex items-start gap-4"
                >
                  <div className="w-8 h-8 rounded-lg bg-gradient-primary flex items-center justify-center flex-shrink-0 text-white">
                    {insight.icon}
                  </div>
                  <p className="text-gray-700 leading-relaxed">{insight.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Outcomes ──────────────────────────────────────────────────────── */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              Give Every Founder{' '}
              <span className="gradient-text">Real Leverage</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {outcomes.map((outcome, i) => (
              <motion.div
                key={outcome}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="card flex items-start gap-4"
              >
                <CheckCircle className="w-6 h-6 text-accent-500 flex-shrink-0 mt-0.5" />
                <span className="font-semibold text-gray-900">{outcome}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Workflow ──────────────────────────────────────────────────────── */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-hero opacity-50" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              Simple Enough to Start Today.{' '}
              <span className="gradient-text">Built to Scale With You.</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {workflowSteps.map((step, i) => (
              <motion.div
                key={step.label}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="relative flex flex-col items-center text-center"
              >
                <div className="w-14 h-14 rounded-2xl bg-gradient-primary flex items-center justify-center shadow-glow-sm text-white mb-3">
                  {step.icon}
                </div>
                <p className="text-sm font-medium text-gray-700 leading-snug">{step.label}</p>
                {i < workflowSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-7 left-full w-4 -translate-x-2">
                    <ArrowRight className="w-4 h-4 text-gray-300" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.7 }}
            className="text-center text-gray-500 mt-12 text-lg"
          >
            Rithmo is the Chief of Staff, COO, and HR advisor your company needs, without the overhead of hiring all three.
          </motion.p>
        </div>
      </section>

      {/* ── Security ─────────────────────────────────────────────────────── */}
      <section className="py-24 bg-gray-50 relative overflow-hidden">
        <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-primary-500/5 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block text-xs font-semibold tracking-widest uppercase text-accent-600 mb-4">
              Security and Trust
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              Built to Pass the{' '}
              <span className="gradient-text">Security Review</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Organizational intelligence is only valuable when it's trusted. Rithmo is designed to
              satisfy security and procurement teams, not just founders.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: <Shield className="w-6 h-6" />,
                title: 'Data Never Sold',
                body: 'Conversation content belongs to your organization. Rithmo does not sell, license, or share meeting data with any third party under any circumstances.',
              },
              {
                icon: <Lock className="w-6 h-6" />,
                title: 'Encrypted at Rest and in Transit',
                body: 'All data is encrypted using AES-256 at rest and TLS 1.3 in transit. Meeting content is never stored as raw recordings.',
              },
              {
                icon: <EyeOff className="w-6 h-6" />,
                title: 'No Recording Retention',
                body: 'Rithmo analyzes meetings for signals. Raw audio is processed in flight and never written to permanent storage.',
              },
              {
                icon: <Users className="w-6 h-6" />,
                title: 'Fully Organization Scoped',
                body: 'Data is completely isolated to your organization. Nothing is shared across customers or accessible by other teams using Rithmo.',
              },
              {
                icon: <CheckCircle className="w-6 h-6" />,
                title: 'Revocable Access',
                body: 'Rithmo connects via Zoom OAuth. Any Zoom admin can disconnect access immediately from the admin dashboard without contacting support.',
              },
              {
                icon: <FileText className="w-6 h-6" />,
                title: 'Compliance Minded by Design',
                body: 'Rithmo is built with SOC 2 principles in mind. Security documentation and data processing agreements are available upon request.',
              },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.07 }}
                className="bg-white rounded-2xl p-6 border border-gray-100 hover:border-accent-500/30 hover:shadow-md transition-all duration-300"
              >
                <div className="w-11 h-11 rounded-xl bg-gradient-primary flex items-center justify-center text-white mb-4 shadow-glow-sm">
                  {item.icon}
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{item.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Future Vision ─────────────────────────────────────────────────── */}
      <section className="py-24 bg-gradient-dark relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            background:
              'radial-gradient(ellipse at 60% 50%, rgba(22,193,178,0.15) 0%, transparent 60%)',
          }}
        />

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="w-16 h-16 rounded-2xl bg-gradient-primary flex items-center justify-center mx-auto mb-6 shadow-glow">
              <Zap className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              What No Founder Has Ever Had.{' '}
              <span className="gradient-text">Until Now.</span>
            </h2>
            <p className="text-lg text-dark-100 leading-relaxed">
              Today, Rithmo briefs you on everything that happens while you're gone. The next step
              is already in development: an Executive AI Proxy. Not just briefing you on
              what happened in meetings you skipped, but representing you in meetings where your
              best judgment matters more than your physical presence. Your values, your
              decision making framework, your communication style, present in every room, without
              taking you away from the work only you can do. Rithmo isn't just a Chief of Staff.
              It's the first version of that future.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────────────────────────── */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative glass rounded-3xl p-12 text-center overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-primary opacity-5" />
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-64 bg-accent-500/20 rounded-full blur-3xl" />

            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                The Chief of Staff You Always Needed. Available From Day One.
              </h2>
              <p className="text-lg text-gray-600 mb-8 max-w-xl mx-auto">
                Rithmo gives founders the organizational intelligence that used to require a team
                to produce. Join the waitlist and be among the first to experience it.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link to="/signup" className="btn-primary text-lg px-8 py-4">
                  Get Early Access
                </Link>
                <Link to="/contact" className="btn-secondary text-lg px-8 py-4">
                  Talk To The Team
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── FAQ ───────────────────────────────────────────────────────────── */}
      <section className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="card divide-y divide-gray-100"
          >
            {faqs.map((faq) => (
              <FaqItem key={faq.question} question={faq.question} answer={faq.answer} />
            ))}
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default FoundersPage
